import { createFileRoute, useSearch } from "@tanstack/react-router";
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { useEffect, useMemo, useState } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useLocation } from "@tanstack/react-router";
import { RenataPanel } from "@/components/renata/RenataPanel";
import { ScreenGuide } from "@/components/renata/ScreenGuide";
import { ModuleTour } from "@/components/renata/ModuleTour";
import {
  getRenataWorkspaceFocus,
  subscribeRenataWorkspaceFocus,
  type RenataWorkspaceFocus,
} from "@/lib/renata/workspace-focus";

export const Route = createFileRoute("/app/renata/chat")({
  validateSearch: (s: Record<string, unknown>) => ({
    c: typeof s.c === "string" ? s.c : undefined,
    task: typeof s.task === "string" ? s.task : undefined,
    route: typeof s.route === "string" ? s.route : undefined,
    entity: typeof s.entity === "string" ? s.entity : undefined,
    entityId: typeof s.entityId === "string" ? s.entityId : undefined,
  }),
  component: RenataChatPage,
  head: () => ({ meta: [{ title: "Conversación — Renata" }] }),
});

function RenataChatPage() {
  const tenantId = useWorkspaceTenantId();
  const location = useLocation();
  const search = useSearch({ from: "/app/renata/chat" });
  const [moduleFocus, setModuleFocus] = useState<RenataWorkspaceFocus | null>(() =>
    getRenataWorkspaceFocus(),
  );

  useEffect(() => subscribeRenataWorkspaceFocus(() => setModuleFocus(getRenataWorkspaceFocus())), []);

  const workspaceFocus = useMemo<RenataWorkspaceFocus | null>(() => {
    const fromSearch = Boolean(search.entity || search.entityId || search.route);
    if (!fromSearch && !moduleFocus) return null;
    return {
      ...moduleFocus,
      route: search.route ?? moduleFocus?.route ?? location.pathname,
      entity: search.entity ?? moduleFocus?.entity,
      entityId: search.entityId ?? moduleFocus?.entityId ?? null,
      entityName: moduleFocus?.entityName ?? null,
      draft: moduleFocus?.draft,
      field: moduleFocus?.field,
      intent: moduleFocus?.intent ?? (fromSearch ? "contextual_help" : undefined),
      selection: moduleFocus?.selection,
    };
  }, [search.entity, search.entityId, search.route, location.pathname, moduleFocus]);

  if (!tenantId) return null;

  return (
    <RenataFeatureGate featureId="chat">
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 space-y-2 border-b px-6 py-3">
        <h1 className="text-base font-semibold">Conversación</h1>
        <p className="text-xs text-muted-foreground">
          Hablá con Renata como con un empleado del negocio.
          {workspaceFocus?.entity
            ? ` Ya conoce el contexto: ${workspaceFocus.entity}${workspaceFocus.entityId ? ` #${workspaceFocus.entityId}` : ""}.`
            : ""}
        </p>
        <ModuleTour screenId="chat" />
        <ScreenGuide screenId="chat" />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        <RenataPanel
          tenantId={tenantId}
          currentRoute={search.route ?? location.pathname}
          initialConversationId={search.c ?? null}
          initialTaskId={search.task ?? null}
          workspaceFocus={workspaceFocus}
        />
      </div>
    </div>
    </RenataFeatureGate>
  );
}
